import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { ComponentType, ReactNode, useMemo } from 'react'
import { useActivationDeactivationController } from '../components/NodeContents/useActivationDeactivationController'
import { NodeTitleProps } from '../components/TreeView'
import { withLazyLoad } from '../components/withLazyComponent'
import { useHoveredNode } from '../HoveredItemStore'
import { useTreeViewStyles } from '../styles'
import { DataNode } from '../types'
import { getNodeOrderLabel } from '../utils/nodeOrderUtils'
import { useShallowSelector } from '../Workspace2Store'
import { useHyperlinkValidator } from './useTreeHandlers'

/** Interface for TreeDataTransformerParams */
interface TreeDataTransformerParams {
    /** Array of tree nodes */
    treeData: DataNode[]
    /** Object containing CSS classes used for styling the TreeView */
    classes: ReturnType<typeof useTreeViewStyles>
    /** Function to add a new tree node */
    addTreeNode?: (sibling: DataNode) => void
}

/**
 * Type of Transformed node for the tree
 * @param {DataNode} node - Node to be transformed
 * @param {ReactNode} title - Content Item to render
 * @param {string} className - CSS class used for styling the node
 * @param {ReactNode} switcherIcon - Icon to render for expand/collapse button
 * @param {TransformedNode} children - Children of the node
 */
export type TransformedNode = DataNode & {
    title: ReactNode
    className?: string
    switcherIcon?: ReactNode
    icon?: ReactNode
    children?: TransformedNode[]
}

const LazyNodeTitle = withLazyLoad<NodeTitleProps>(() =>
    import('../components/TreeView/NodeTitle').then((module) => ({
        default: module.NodeTitle as ComponentType<any>,
    }))
)

/**
 * Custom hook for transforming tree data into renderable format
 * @param {TreeDataTransformerParams} - Parameters for the hook
 * @returns {TransformedNode[]} Transformed tree data
 */
export const useTreeDataTransformer = ({
    treeData,
    classes,
    addTreeNode,
}: TreeDataTransformerParams): TransformedNode[] => {
    const {
        activeTab,
        articlesLoading,
        expandedKeys,
        contentItemViewNodes,
    } = useShallowSelector((state) => ({
        activeTab: state.activeTab,
        articlesLoading: state.articlesLoading,
        expandedKeys: state.expandedKeys,
        contentItemViewNodes: state.contentItemViewNodes,
    }))

    const {
        disableProductTypeInactivation,
    } = useActivationDeactivationController()
    const hoveredNode = useHoveredNode()
    const { getHyperlinkCount, hasKeyedHyperlink } = useHyperlinkValidator()
    const isContentTeamFromStudioPage = isReactiveContentTeamFromStudioPage()
        ?.value

    const {
        visibilitySettingValues: { areNotesVisible, isInactiveContentVisible },
    } = useShallowEditorActionBar((state) => ({
        visibilitySettingValues: state.visibilitySettingValues,
    }))
    const isHovered = (node: DataNode) =>
        (hoveredNode as DataNode)?.key === node.key
    const nodeClass = (node: DataNode) =>
        clsx(
            node.type === NODE_TYPES.Article && classes.articleContainer,
            isHovered(node) && 'hovered'
        )

    return useMemo(() => {
        const transform = (
            nodes: DataNode[],
            depth: number = 0
        ): TransformedNode[] => {
            // Only visible nodes for order calculation
            const visibleNodes = nodes.filter((node) => !node.isHidden)

            return (isInactiveContentVisible ? nodes : visibleNodes).map(
                (node) => {
                    const orderLabel = getNodeOrderLabel(
                        node,
                        visibleNodes,
                        depth,
                        activeTab,
                        contentItemViewNodes
                    )

                    return {
                        ...node,
                        ...{
                            role: 'treeitem',
                            'data-testid': 'tree-child',
                            'data-key': node.key,
                            'aria-expanded': node.isLeaf ? 'false' : 'true',
                        },
                        className: nodeClass(node),

                        title: (
                            <LazyNodeTitle
                                node={node}
                                addTreeNode={addTreeNode}
                                isExpanded={expandedKeys?.includes(node.key)}
                                showNodeOrder={true}
                                orderLabel={orderLabel}
                                classes={classes}
                                areNotesVisible={areNotesVisible}
                            />
                        ),
                        disabled: isContentTeamFromStudioPage
                            ? false
                            : node.isHidden,
                        children: transform(node.children || [], depth + 1), // Increment depth for children
                    }
                }
            )
        }

        return transform(treeData)
    }, [
        treeData,
        activeTab,
        articlesLoading,
        expandedKeys,
        classes,
        getHyperlinkCount,
        hasKeyedHyperlink,
        hoveredNode,
        disableProductTypeInactivation,
        isInactiveContentVisible,
        areNotesVisible,
    ])
}
